import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@ltvco/refresh-lib/vendors';

import { buildLeadScoreResult } from 'utils/leadScore/buildLeadScoreResult';
import {
  fetchLeadReportsByPermalinks,
  resolveLeadReports,
} from 'utils/leadScore/resolveLeadReports';

import type {
  LeadScoreResultData,
  LeadScoreUseCase,
  ScoreLeadRequest,
  ScoreLeadResponse,
} from 'api/leadScore';

export interface ScoreLeadMutationParams extends ScoreLeadRequest {
  streetLine: string;
  city?: string;
  state?: string;
  zipcode?: string;
  brandLink: string;
}

export interface UseScoreLeadMutationCallbacks {
  onSuccess?: (
    response: ScoreLeadResponse,
    variables: ScoreLeadMutationParams
  ) => void;
  onError?: (error: Error) => void;
}

export const useScoreLeadMutation = (
  callbacks?: UseScoreLeadMutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation<ScoreLeadResponse, Error, ScoreLeadMutationParams>({
    mutationFn: async ({
      streetLine,
      city,
      state,
      zipcode,
      brandLink,
      ...request
    }) => {
      const resolved = await resolveLeadReports({
        fullName: request.full_name,
        address: streetLine,
        city,
        state,
        zipcode,
        brandLink,
        queryClient,
      });

      if (!resolved.subjectProperty) {
        return { success: false };
      }

      const data = buildLeadScoreResult({
        useCase: request.use_case,
        address: request.address,
        propertyReport: resolved.propertyReport,
        subjectProperty: resolved.subjectProperty,
        personReport: resolved.personReport,
        phoneReport: resolved.phoneReport,
        propertyPermalink: resolved.propertyPermalink,
        personPermalink: resolved.personPermalink,
        phonePermalink: resolved.phonePermalink,
        fallbackOwnerName: resolved.resolvedOwnerName,
      });

      return { success: true, data };
    },
    onSuccess: (response, variables) => {
      callbacks?.onSuccess?.(response, variables);
    },
    onError: (error) => {
      callbacks?.onError?.(error);
    },
  });
};

export interface LeadScoreFromPermalinksParams {
  useCase: LeadScoreUseCase;
  address: string;
  propertyPermalink: string;
  personPermalink?: string;
  phonePermalink?: string;
}

export const useLeadScoreFromPermalinks = (
  params: LeadScoreFromPermalinksParams | null,
  placeholderData?: LeadScoreResultData
) => {
  const queryClient = useQueryClient();

  return useQuery<LeadScoreResultData>({
    queryKey: ['leadScore', 'byPermalinks', params],
    placeholderData,
    queryFn: async () => {
      const {
        useCase: leadScoreUseCase,
        address,
        propertyPermalink,
        personPermalink,
        phonePermalink,
      } = params!;

      const fetched = await fetchLeadReportsByPermalinks({
        propertyPermalink,
        personPermalink,
        phonePermalink,
        queryClient,
      });

      return buildLeadScoreResult({
        useCase: leadScoreUseCase,
        address,
        propertyReport: fetched.propertyReport,
        subjectProperty: fetched.subjectProperty,
        personReport: fetched.personReport,
        phoneReport: fetched.phoneReport,
        propertyPermalink,
        personPermalink,
        phonePermalink,
      });
    },
    enabled: !!params,
  });
};
